'use client'

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/css-utils'
import { TopNavProps } from '@/components/sections/top-nav/utils'
import NextLink from 'next/link'
import { MainRoutes } from '@/utils/routes'
import { Logo } from '@/components/sections/logo'
import { Link } from '@/components/ui/deprecated-link'
import { LogoutLink } from '@/components/sections/logout-link'
import { useState } from 'react'
import { NavSignupButton } from './nav-signup-button'
import { NavSigninButton } from './nav-signin-button'
import { UserAvatar } from '@/components/sections/top-nav/user-avatar'
import { getGlobalNavItems } from '../get-global-nav-items'

function UnAuthorizedContent() {
  return (
    <div className="flex flex-col w-full gap-3 p-3">
      <NavSignupButton navType="mobile" />
      <NavSigninButton navType="mobile" />
    </div>
  )
}

function AuthorizedContent({
  userNavItems,
  onClose,
}: TopNavProps & { onClose: (href?: string) => void }) {
  return (
    <>
      <ul className="px-2">
        {userNavItems.map(({ href, name, isExternal }) => (
          <li key={name} className="p-3">
            <Link
              href={href}
              isExternal={isExternal}
              className="text-c-14 font-medium text-copy-onMuted"
              onClick={() => {
                onClose(href)
              }}
            >
              {name}
            </Link>
          </li>
        ))}
        <li className="p-3">
          <LogoutLink
            hasIcon={false}
            className="font-medium text-copy-onMuted"
          />
        </li>
      </ul>
      <div className="px-2 py-3">
        <div className="w-full h-px bg-background-divider" />
      </div>
      <div className="flex flex-col w-full gap-3 p-3">
        <Link
          href={MainRoutes.Invite}
          variant="primaryButton"
          className="w-full font-bold"
          size="lg"
        >
          Invite Friends
        </Link>
      </div>
    </>
  )
}

function UsernameSection({
  userProfile,
  userAvatarUrl,
  onClose,
}: TopNavProps & { onClose: () => void }) {
  return (
    <Link
      className="flex items-center gap-2"
      href={MainRoutes.Profile}
      onClick={onClose}
    >
      <UserAvatar userAvatarUrl={userAvatarUrl} userProfile={userProfile} />
      <span className="text-c-18 font-bold text-copy-primary">
        {userProfile?.firstName}
      </span>
    </Link>
  )
}

export function MobileTopNav(props: TopNavProps) {
  const { isOnDark, isAuthenticated, pathname } = props
  const globalNavItems = getGlobalNavItems({
    isAuthenticated: props.isAuthenticated,
  })
  const [isOpen, setIsOpen] = useState(false)

  const onMenuItemClick = (href?: string) => {
    const [cleanHref, hash] = href?.split('#') ?? []
    if (href && pathname.startsWith(cleanHref)) {
      setIsOpen(false)

      /**
       * Little hacky solution
       * Since drawer is scrolling to top after closing it, we need to scroll
       * back to section when user is already on that page and the link was just
       * a hash update. e.g. Feedback section
       */
      if (hash) {
        setTimeout(() => {
          document.getElementById(hash)?.scrollIntoView({
            behavior: 'instant',
            block: 'center',
          })
        }, 800)
      }
    }
  }

  return (
    <div className="flex lg:hidden justify-end align-center h-full">
      <Drawer
        dismissible
        direction="right"
        fadeFromIndex={1}
        snapPoints={[0, 1]}
        activeSnapPoint={1}
        modal={false}
        onOpenChange={setIsOpen}
        open={isOpen}
      >
        <DrawerTrigger asChild>
          <button>
            <Menu
              className={cn('text-copy-primary', {
                'text-black-100': isOnDark,
              })}
            />
          </button>
        </DrawerTrigger>
        <DrawerContent className="bg-background-page border-topNav-divider h-dvh flex flex-col inset-0 mt-0">
          <div
            className={cn('flex w-full justify-between py-[14px] px-5', {
              'py-4': isAuthenticated,
            })}
          >
            {isAuthenticated ? (
              <UsernameSection
                {...props}
                onClose={() => {
                  onMenuItemClick(MainRoutes.Profile)
                }}
              />
            ) : (
              <NextLink
                href={MainRoutes.Home}
                className="w-fit block"
                onClick={() => {
                  onMenuItemClick(MainRoutes.Home)
                }}
                scroll={false}
                prefetch={false}
              >
                <Logo hasName priority />
              </NextLink>
            )}
            <DrawerTitle className="sr-only">Menu</DrawerTitle>
            <DrawerClose>
              <X className="text-copy-primary hover:text-copy-accent w-8 h-8" />
            </DrawerClose>
          </div>

          <ul className="px-2 mt-2">
            {globalNavItems.map(({ href, name, isExternal }) => (
              <li key={name} className="p-3">
                <Link
                  href={href}
                  isExternal={isExternal}
                  className="font-bold text-copy-onMuted"
                  size="lg"
                  onClick={() => {
                    onMenuItemClick(href)
                  }}
                  scroll={false}
                >
                  {name}
                </Link>
              </li>
            ))}
          </ul>
          <div className="px-2 py-3">
            <div className="w-full h-px bg-background-divider" />
          </div>
          {isAuthenticated ? (
            <AuthorizedContent {...props} onClose={onMenuItemClick} />
          ) : (
            <UnAuthorizedContent />
          )}
        </DrawerContent>
      </Drawer>
    </div>
  )
}
